/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, RefreshControl } from 'react-native'
import { useFocusEffect } from '@react-navigation/native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import { ActiveMinutes } from '@dialogue/services'
import type { ActivityType, TrackerName } from '@dialogue/services/dist/active-minutes/model'
import Toast from 'react-native-toast-message'
import { useTheme } from 'styled-components/native'

import ActiveMinutesActions from 'APP/Redux/ActiveMinutesRedux'
import { useRefreshControlHandlers } from 'APP/Hooks/RefreshControlHandlers'
import type { TerminateModalHandle } from 'APP/Hooks/TerminateModal'
import {
  comingSoonTrackers,
  deprecatedTrackers,
  supportedTrackerActivities,
  supportedTrackers,
} from 'APP/Lib/ActiveMinutes/trackers/constants'
import { getColourScheme } from 'APP/Helpers/AppearanceUtils'
import Analytics from 'APP/Services/Analytics'
import I18n, { isTranslationKey } from 'APP/I18n'
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import { ResponsiveScrollView } from 'APP/Converse/Layout'
import Typography from 'APP/Converse/Typography'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import DialogueTouchableOpacity from 'APP/Components/DialogueTouchableOpacity'

import ProviderIntroModal from './ProviderIntroModal'
import TrackerSection from './TrackerSection'
import Styles from './style'
import { canCreateNewConnection } from './utils'
import {
  SectionKey,
  type HealthTrackerSettingsScreenProps,
  type TrackerListItem,
  type TrackerSectionConfig,
} from './types'

export type { HealthTrackerSettingsScreenProps } from './types'

const SECTIONS: TrackerSectionConfig[] = [
  {
    key: SectionKey.CONNECTED,
    title: I18n.t('HealthTrackerSettings.sections.usersTrackers'),
    ctaStatusMap: {
      [ActiveMinutes.Types.TrackerStatus.Connected]: I18n.t(
        'HealthTrackerSettings.ctas.disconnect'
      ),
      [ActiveMinutes.Types.TrackerStatus.Broken]: I18n.t('HealthTrackerSettings.ctas.reconnect'),
    },
  },
  {
    key: SectionKey.SUPPORTED,
    title: I18n.t('HealthTrackerSettings.sections.allTrackers'),
    ctaStatusMap: {
      default: I18n.t('HealthTrackerSettings.ctas.connect'),
    },
    trackers: supportedTrackers,
  },
  {
    key: SectionKey.COMING_SOON,
    title: I18n.t('HealthTrackerSettings.sections.comingSoon'),
    ctaStatusMap: {
      default: I18n.t('HealthTrackerSettings.ctas.request'),
    },
    trackers: comingSoonTrackers,
  },
]

const HealthTrackerSettingsScreen: React.FC<HealthTrackerSettingsScreenProps> = ({
  navigation,
  route,
}) => {
  const theme = useTheme()
  const providerInfoModalRef = useRef<TerminateModalHandle>(null)
  const dispatch = useDispatch()
  const getConnectedTrackers = useCallback(
    () => dispatch(ActiveMinutesActions.getConnectedTrackers()),
    []
  )
  const requestNewConnection = (provider: TrackerName | string) =>
    dispatch(ActiveMinutesActions.requestNewConnection(provider))
  const resetRequestNewConnection = () => dispatch(ActiveMinutesActions.resetRequestNewConnection())
  const removeConnection = (provider: TrackerName | string, id?: string) =>
    dispatch(ActiveMinutesActions.removeConnection(provider, id))
  const {
    loading,
    retry,
    data: connectedTrackers,
  } = useSelector((state) => state.activeMinutes.connectedTrackers)
  const newConnectionRequest = useSelector((state) => state.activeMinutes.newConnectionRequest)
  const removeConnectionState = useSelector((state) => state.activeMinutes.removeConnection)
  const refreshControlHandlers = useRefreshControlHandlers(connectedTrackers, getConnectedTrackers)
  const [providerInfoModal, setProviderInfoModal] = useState<string | null | undefined>(null)
  const requestedActivity = route?.params?.requestedActivity as ActivityType | undefined

  useFocusEffect(
    useCallback(() => {
      getConnectedTrackers()
    }, [])
  )

  useEffect(() => {
    // propagate redux error to navigation params
    // to handle all errors in one place
    if (newConnectionRequest.error) {
      navigation.setParams({ error: newConnectionRequest.error })
      resetRequestNewConnection()
    }
  }, [newConnectionRequest.error])
  useEffect(() => {
    // if the outcome of the connection request is an established connection (a case for SDK providers)
    // propagate success to navigation params
    // to handle all success banners in one place
    if (newConnectionRequest.connectionCreated) {
      navigation.setParams({ success: true, provider: newConnectionRequest.provider })
      resetRequestNewConnection()
    }
  }, [newConnectionRequest.connectionCreated])
  // handle success/error banners
  useEffect(() => {
    const toastCleanUp = {
      onShow: () =>
        navigation.setParams({
          success: undefined,
          error: undefined,
          provider: undefined,
        }),
    }

    if (route.params?.success) {
      // refresh connected trackers
      getConnectedTrackers()

      const successKey = `HealthTrackerSettings.connectionResolution.success.body.${route?.params?.provider}`
      const deafaultKey = 'HealthTrackerSettings.connectionResolution.success.body.default'

      Toast.show({
        text1: I18n.t('HealthTrackerSettings.connectionResolution.success.title'),
        text2: I18n.t(isTranslationKey(successKey) ? successKey : deafaultKey),
        ...toastCleanUp,
      })
      Analytics.trackEvent('banner_view', { banner_name: 'Tracker successful banner' })
    }

    if (route.params?.error) {
      const errorKey = `HealthTrackerSettings.connectionResolution.error.body.${route.params.error}`
      const deafaultKey = 'HealthTrackerSettings.connectionResolution.error.body.default'

      Toast.show({
        text1: I18n.t('HealthTrackerSettings.connectionResolution.error.title'),
        text2: I18n.t(isTranslationKey(errorKey) ? errorKey : deafaultKey),
        ...toastCleanUp,
      })
      Analytics.trackEvent('banner_view', { banner_name: 'Tracker error banner' })
    }
  }, [route.params])

  const connectedAndSupportedTrackers = useMemo(() => {
    // Deprecated trackers should still be visible to the member if they are already connected [DIA-82488]
    const visibleTrackers = [...supportedTrackers, ...deprecatedTrackers]

    return connectedTrackers.filter(({ provider }) =>
      visibleTrackers.includes(provider as TrackerName)
    )
  }, [connectedTrackers])

  const trackerSections = useMemo(() => {
    const isTrackerAlreadyConnected = (provider: TrackerName | string) => {
      return connectedAndSupportedTrackers.some(
        ({ provider: connectedProvider }) => connectedProvider === provider
      )
    }

    const getAvailableTrackers = (trackersList: TrackerName[]): TrackerListItem[] => {
      return trackersList
        .filter((provider) => !isTrackerAlreadyConnected(provider))
        .map((provider) => ({ provider }))
    }

    return SECTIONS.map((section) => ({
      ...section,
      trackers:
        section.key === SectionKey.CONNECTED
          ? connectedAndSupportedTrackers
          : getAvailableTrackers(section.trackers || []),
    }))
  }, [connectedAndSupportedTrackers])

  const handleHelpPress = () => {
    const uri = UrlBuilder.cmsConsultUrl('healthTrackers', getColourScheme())
    navigation.navigate('cmsScreen', { uri, screen: 'cms', showShareButton: false })
  }

  const handleTrackerCtaPress = (sectionKey: SectionKey, provider: TrackerName | string) => {
    if (
      sectionKey === SectionKey.SUPPORTED &&
      I18n.exists(`HealthTrackerSettings.modal.${provider}`)
    ) {
      setProviderInfoModal(provider)
    }

    if (sectionKey === SectionKey.COMING_SOON) {
      Analytics.trackEvent('button_click', {
        button_value: provider,
        trigger: 'request_tracker',
      })
      Toast.show({
        text1: I18n.t('HealthTrackerSettings.connectionResolution.request.title'),
        text2: I18n.t('HealthTrackerSettings.connectionResolution.request.body'),
      })
    }

    if (sectionKey === SectionKey.CONNECTED) {
      Analytics.trackEvent('button_click', {
        button_value: provider,
        trigger: 'disconnect_tracker',
      })
      removeConnection(provider, connectedTrackers.find(({ provider: p }) => p === provider)?.id)
    }
  }

  const handleTrackerIntroConnect = async (provider: TrackerName | string) => {
    await providerInfoModalRef.current?.terminateModal()

    if (await canCreateNewConnection(provider as TrackerName)) {
      requestNewConnection(provider)
    }
    Analytics.trackEvent('button_click', {
      button_value: provider,
      trigger: 'connect_tracker',
    })
  }

  return (
    <NoticeBarContainer>
      <ResponsiveScrollView
        refreshControl={<RefreshControl {...refreshControlHandlers} />}
        testID="SCROLL"
      >
        {!loading && retry && (
          <DialogueTouchableOpacity
            style={Styles.refresh}
            onPress={() => getConnectedTrackers()}
            analyticsName="Refresh"
            testID="RETRY"
          >
            <MaterialIcon
              name="refresh"
              size={theme.metrics.icon.large}
              color={theme.colors.text}
            />
          </DialogueTouchableOpacity>
        )}
        {!retry && (
          <View>
            <DialogueTouchableOpacity
              style={Styles.help}
              testID="HELP"
              onPress={handleHelpPress}
              analyticsName="How trackers work"
            >
              <Typography variant="subheaderBold">
                {I18n.t('HealthTrackerSettings.help')}
              </Typography>
              <MaterialIcon
                name="arrow-forward-ios"
                size={theme.metrics.icon.tiny}
                color={theme.colors.accent}
              />
            </DialogueTouchableOpacity>
            {trackerSections.map(({ key, title, trackers, ctaStatusMap }) => (
              <View key={key}>
                {trackers.length > 0 && (
                  <Typography
                    variant="captionBold"
                    color="secondary"
                    testID="SECTION_TITLE"
                    style={Styles.header}
                  >
                    {title}
                  </Typography>
                )}
                {trackers.map((tracker) => {
                  const supportsRequestedActivity =
                    !!requestedActivity && key !== SectionKey.COMING_SOON
                      ? !!supportedTrackerActivities[tracker.provider as TrackerName]?.includes(
                          requestedActivity
                        )
                      : true

                  const modifying =
                    (newConnectionRequest.provider === tracker.provider &&
                      newConnectionRequest.processing) ||
                    (removeConnectionState.provider === tracker.provider &&
                      removeConnectionState.processing)

                  const disabled =
                    newConnectionRequest.processing || removeConnectionState.processing

                  const deprecated = deprecatedTrackers.includes(tracker.provider as TrackerName)

                  return (
                    <TrackerSection
                      key={tracker.provider}
                      supported={supportsRequestedActivity}
                      deprecated={deprecated}
                      ctaStatusMap={ctaStatusMap}
                      disabled={disabled}
                      modifying={modifying}
                      onPress={() => handleTrackerCtaPress(key, tracker.provider)}
                      {...tracker}
                    />
                  )
                })}
              </View>
            ))}
          </View>
        )}
      </ResponsiveScrollView>
      <ProviderIntroModal
        ref={providerInfoModalRef}
        // @ts-expect-error: ProviderIntroModal.js is untyped. TODO: Remove once migrated to TS.
        provider={providerInfoModal}
        onClose={() => setProviderInfoModal(undefined)}
        onConnect={handleTrackerIntroConnect}
      />
    </NoticeBarContainer>
  )
}

export default HealthTrackerSettingsScreen
